import { Icon, Menu as MenuIcon } from "@norican/icons";
import { Badge, Button, cn, Drawer, Separator, Typography } from "@norican/react";
import { useState } from "react";
import { Link, useLocation } from "react-router";

import { useT } from "@/i18n";
import { useMe } from "@/lib/api";

import { activeNavItem, type NavItem, primaryNavItemsFor } from "./nav-items";
import { useItAdminNav } from "./use-it-admin-nav";

export interface MobileNavProps {
  className?: string;
}

/**
 * Navigation for narrow viewports.
 *
 * The design system has no app-shell or sidebar component, so the mobile menu
 * is a left-anchored `Drawer` — a modal surface with focus trapping and
 * swipe-to-dismiss already handled. Selecting a destination closes it, which
 * the drawer cannot know to do on its own because routing happens outside it.
 *
 * Same content as the desktop bar (6 Oct, decision 35): "New starters" where
 * it applies, then the IT Admin tools under the same group headings as the
 * dropdown, with the same badge.
 */
export function MobileNav({ className }: MobileNavProps) {
  const { t } = useT();
  const { pathname, search } = useLocation();
  const [open, setOpen] = useState(false);

  const { data: me } = useMe();
  const primary = primaryNavItemsFor(me);
  const { groups, items, pending } = useItAdminNav();
  const current = activeNavItem([...primary, ...items], pathname, search);

  // Nothing to put in the drawer for a regular user, and a trigger that opens
  // an empty panel is worse than no trigger: the header keeps its logo, My
  // tickets, language and identity, which is the whole menu for them.
  if (primary.length === 0 && groups.length === 0) {
    return null;
  }

  const link = (item: NavItem) => {
    const active = current?.id === item.id;
    return (
      <li key={item.id}>
        <Link
          to={item.to}
          aria-current={active ? "page" : undefined}
          onClick={() => setOpen(false)}
          className={cn(
            "flex items-center gap-3 rounded-md px-3 py-3 text-base font-medium",
            active ? "text-accent-fg-subtle bg-accent-subtle" : "text-fg hover:bg-surface-hover",
          )}
        >
          <Icon icon={item.icon} size="md" />
          <span className="flex-1">{t(item.labelKey)}</span>
          {item.badge === "access" && pending > 0 ? (
            <Badge tone="accent" variant="solid" size="xs">
              {pending}
            </Badge>
          ) : null}
        </Link>
      </li>
    );
  };

  return (
    <Drawer.Root side="left" open={open} onOpenChange={setOpen}>
      <Drawer.Trigger
        className={className}
        render={
          <Button
            variant="ghost"
            tone="neutral"
            size="sm"
            iconOnly
            aria-label={
              pending > 0 ? t("itAdminMenu.triggerPending", { n: pending }) : t("nav.open")
            }
            startIcon={<Icon icon={MenuIcon} size="md" />}
          />
        }
      />
      <Drawer.Portal>
        <Drawer.Backdrop />
        <Drawer.Viewport>
          <Drawer.Popup>
            <Drawer.Header>
              <Drawer.Title>{t("nav.menu")}</Drawer.Title>
              <Drawer.CloseButton label={t("nav.close")} />
            </Drawer.Header>
            <Separator />
            <Drawer.Body>
              <nav aria-label={t("nav.label")} className="flex flex-col gap-4">
                {primary.length > 0 ? (
                  <ul className="flex flex-col gap-1">{primary.map(link)}</ul>
                ) : null}
                {groups.length > 0 ? (
                  <Typography as="p" variant="body-sm" className="text-fg px-3 font-semibold">
                    {t("nav.itAdmin")}
                  </Typography>
                ) : null}
                {groups.map((group, index) => (
                  <section
                    key={group.id}
                    aria-labelledby={`mobile-nav-${group.id}`}
                    className={index > 0 ? "border-border-strong border-t pt-3" : undefined}
                  >
                    <Typography
                      as="h2"
                      variant="caption"
                      id={`mobile-nav-${group.id}`}
                      className="text-accent px-3 pb-1 font-bold tracking-widest uppercase"
                    >
                      {t(group.labelKey)}
                    </Typography>
                    <ul className="flex flex-col gap-1">{group.items.map(link)}</ul>
                  </section>
                ))}
              </nav>
            </Drawer.Body>
          </Drawer.Popup>
        </Drawer.Viewport>
      </Drawer.Portal>
    </Drawer.Root>
  );
}
